import React, { useState, useEffect, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { DragIndicatorIcon, AddIcon, EditIcon, DeleteIcon } from "../../icons";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import "../../styles/common/matchViewer.scss";
import { parseJsonRobustly } from "../../helper/jsonUtils";
import GlobalLoader from "./GlobalLoader";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";

const MatchViewer = ({
  questionData,
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  contentIndex,
  onAddNext,
  title = "Match the Following",
  permission,
  allowEditWithoutPersist = false,
  startInEditMode = false,
  externalSaveSignal,
  externalCancelSignal,
  provideUpdatedBuilder,
}) => {
  const [items, setItems] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [editMode, setEditMode] = useState(!!startInEditMode);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [category, setCategory] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const controls = useDragControls();
  const elementRef = useRef();
  const editSnapshotRef = useRef(null);

  useEffect(() => {
    setEditMode(!!startInEditMode);
  }, [startInEditMode]);

  useEffect(() => {
    setItems([]);
    setInitialData([]);
    setIsSubmitted(false);
    setShowAnswer(false);
    setExplanation("");
    editSnapshotRef.current = null;

    if (!questionData) return;

    try {
      let parsedData = questionData;

      if (typeof questionData === "string") {
        parsedData = parseJsonRobustly(questionData);
      }

      if (parsedData && typeof parsedData === "object" && parsedData.content) {
        if (typeof parsedData.content === "string") {
          parsedData.content = parseJsonRobustly(parsedData.content);
        }
        parsedData = parsedData.content;
      }

      if (
        parsedData &&
        typeof parsedData === "object" &&
        Array.isArray(parsedData.questions)
      ) {
        const questions = parsedData.questions;

        if (
          questions.length >= 1 &&
          questions[0] &&
          Array.isArray(questions[0].pairs) &&
          questions[0].pairs.length >= 2
        ) {
          const base = questions[0];
          const matchData = base.pairs;
          setItemsDirectly(matchData);
          if (typeof base.explanation === "string") {
            setExplanation(base.explanation);
          } else if (parsedData.explanation) {
            setExplanation(parsedData.explanation);
          }
          if (base.categories || parsedData.categories) {
            setCategory(base.categories || parsedData.categories);
          }
          return;
        }

        if (questions.length >= 2) {
          const column1 = questions[0];
          const column2 = questions[1];
          if (
            column1.key &&
            Array.isArray(column1.values) &&
            column2.key &&
            Array.isArray(column2.values)
          ) {
            const matchData = [column1, column2];
            setItemsDirectly(matchData);
            if (questions.length > 2 && questions[2].explanation) {
              setExplanation(questions[2].explanation);
            } else if (parsedData.explanation) {
              setExplanation(parsedData.explanation);
            }
            return;
          }
        }
      }

      if (
        parsedData &&
        typeof parsedData === "object" &&
        Array.isArray(parsedData.pairs) &&
        parsedData.pairs.length >= 2
      ) {
        const base = parsedData;
        const matchData = base.pairs;
        setItemsDirectly(matchData);
        if (typeof base.explanation === "string") {
          setExplanation(base.explanation);
        }
        if (base.categories) {
          setCategory(base.categories);
        }
        return;
      }

      if (Array.isArray(parsedData)) {
        if (
          parsedData.length >= 2 &&
          typeof parsedData[0] === "object" &&
          typeof parsedData[1] === "object" &&
          (parsedData[0].key || parsedData[0].title) &&
          Array.isArray(parsedData[0].values)
        ) {
          setItemsDirectly(parsedData);
          const last = parsedData[parsedData.length - 1];
          if (last && typeof last === "object" && last.explanation) {
            setExplanation(last.explanation);
          }
        } else if (parsedData.length > 0) {
          const hasExplanation =
            parsedData.length > 2 &&
            typeof parsedData[parsedData.length - 1] === "object" &&
            parsedData[parsedData.length - 1].explanation;

          const dataToProcess = hasExplanation
            ? parsedData.slice(0, -1)
            : parsedData;
          const explanationText = hasExplanation
            ? parsedData[parsedData.length - 1].explanation
            : "";

          setExplanation(explanationText);

          if (dataToProcess.length % 2 === 0 && dataToProcess.length > 0) {
            const midpoint = Math.floor(dataToProcess.length / 2);
            const leftColumn = {
              key: "Column 1",
              values: dataToProcess.slice(0, midpoint),
            };
            const rightColumn = {
              key: "Column 2",
              values: dataToProcess.slice(midpoint),
            };
            const matchData = [leftColumn, rightColumn];
            shuffle(matchData);
          } else if (dataToProcess.length > 0) {
            const leftValues = [];
            const rightValues = [];
            for (let i = 0; i < dataToProcess.length; i += 2) {
              leftValues.push(dataToProcess[i]);
              if (i + 1 < dataToProcess.length) {
                rightValues.push(dataToProcess[i + 1]);
              }
            }
            const matchData = [
              { key: "Terms", values: leftValues },
              { key: "Definitions", values: rightValues },
            ];
            shuffle(matchData);
          } else {
            setItems([]);
            setInitialData([]);
          }
        } else {
          setItems([]);
          setInitialData([]);
        }
      } else if (
        parsedData &&
        typeof parsedData === "object" &&
        (parsedData.items || parsedData.columns)
      ) {
        const dataToUse = parsedData.items || parsedData.columns;
        if (Array.isArray(dataToUse) && dataToUse.length >= 2) {
          setItemsDirectly(dataToUse);
          setExplanation(parsedData.explanation || "");
        }
      } else if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.left &&
        parsedData.right
      ) {
        const matchData = [
          {
            key: "Left Column",
            values: Array.isArray(parsedData.left) ? parsedData.left : [],
          },
          {
            key: "Right Column",
            values: Array.isArray(parsedData.right) ? parsedData.right : [],
          },
        ];
        setExplanation(parsedData.explanation || "");
        setItemsDirectly(matchData);
      } else if (parsedData && typeof parsedData === "object") {
        setExplanation(parsedData.explanation || "");
        const keys = Object.keys(parsedData);
        if (keys.length >= 2) {
          const matchData = keys.slice(0, 2).map((key) => ({
            key: key.charAt(0).toUpperCase() + key.slice(1),
            values: Array.isArray(parsedData[key]) ? parsedData[key] : [],
          }));
          setItemsDirectly(matchData);
        }
      } else if (
        typeof questionData === "string" &&
        questionData.trim() !== ""
      ) {
        try {
          const jsonData = JSON.parse(questionData);
          if (Array.isArray(jsonData)) {
            setItemsDirectly(jsonData);
          } else {
            setItems([]);
          }
        } catch (e) {
          setItemsDirectly([
            { key: "Terms", values: [questionData] },
            { key: "Definitions", values: [""] },
          ]);
        }
      } else {
        setItems([]);
        setInitialData([]);
      }
    } catch (error) {
      console.error("Error parsing Match data:", error);
      setItems([]);
    }
  }, [questionData]);

  useEffect(() => {
    if (!externalSaveSignal) return;
    if (typeof onUpdate !== "function") return;
    try {
      const updatedItem = buildUpdatedItemFromOriginal(questionData);
      onUpdate({ content: [updatedItem] });
    } catch (e) {
      console.log(e);
    }
  }, [externalSaveSignal]);

  useEffect(() => {
    if (typeof provideUpdatedBuilder === "function") {
      provideUpdatedBuilder((originalItem) =>
        buildUpdatedItemFromOriginal(originalItem ?? questionData),
      );
    }
  }, [provideUpdatedBuilder, items, explanation, category]);

  useEffect(() => {
    if (!externalCancelSignal) return;
    restoreEditSnapshot();
    setEditMode(!!startInEditMode);
  }, [externalCancelSignal]);

  const startEditing = () => {
    editSnapshotRef.current = {
      items: JSON.parse(JSON.stringify(items || [])),
      explanation,
      category,
    };
    setEditMode(true);
  };

  const restoreEditSnapshot = () => {
    const snapshot = editSnapshotRef.current;
    if (snapshot) {
      setItems(JSON.parse(JSON.stringify(snapshot.items)));
      setExplanation(snapshot.explanation);
      setCategory(snapshot.category);
    }
    editSnapshotRef.current = null;
  };

  const cancelEditing = () => {
    restoreEditSnapshot();
    setEditMode(false);
  };

  function shuffle(data) {
    if (!Array.isArray(data) || data.length < 2) return;
    setInitialData(JSON.parse(JSON.stringify(data)));
    const leftCol = data[0];
    const rightCol = data[1];

    if (!rightCol?.values || !Array.isArray(rightCol.values)) {
      setItems(data);
      return;
    }

    let shuffledArray = [...rightCol.values];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [
        shuffledArray[j],
        shuffledArray[i],
      ];
    }

    setItems([leftCol, { key: rightCol.key, values: shuffledArray }]);
  }

  const setItemsDirectly = (data) => {
    if (!Array.isArray(data) || data.length < 2) {
      setItems([]);
      setInitialData([]);
      return;
    }
    setInitialData(JSON.parse(JSON.stringify(data)));
    setItems(data);
  };

  const buildUpdatedItemFromOriginal = (originalItem) => {
    let orig;
    if (typeof originalItem === "string") {
      try {
        orig = JSON.parse(originalItem);
      } catch (e) {
        orig = {};
      }
    } else {
      const cloned = JSON.parse(JSON.stringify(originalItem || {}));
      orig = cloned && typeof cloned === "object" ? cloned : {};
    }

    if (Array.isArray(orig.pairs)) {
      const updatedPairs = (items || []).map((it) => ({
        key: it?.key || "",
        values: Array.isArray(it?.values) ? [...it.values] : [],
      }));
      return {
        ...orig,
        pairs: updatedPairs,
        ...(explanation !== undefined ? { explanation } : {}),
        type: orig.type || "match",
      };
    }

    if (orig && typeof orig.content !== "undefined") {
      let cont = orig.content;
      if (typeof cont === "string") {
        try {
          cont = JSON.parse(cont);
        } catch (_) {
          cont = {};
        }
      }
      if (cont && typeof cont === "object" && Array.isArray(cont.pairs)) {
        const updatedPairs = (items || []).map((it) => ({
          key: it?.key || "",
          values: Array.isArray(it?.values) ? [...it.values] : [],
        }));
        const nextContent = {
          ...cont,
          pairs: updatedPairs,
          ...(explanation !== undefined ? { explanation } : {}),
          type: cont.type || "match",
        };
        return {
          ...orig,
          content: JSON.stringify(nextContent),
        };
      }
    }

    const legacyContent = {
      questions: [
        ...items.map((it) => ({
          key: it.key || "",
          values: Array.isArray(it.values) ? [...it.values] : [],
        })),
        ...(explanation ? [{ explanation }] : []),
      ],
    };
    return {
      ...orig,
      type: "match",
      content: JSON.stringify(legacyContent),
    };
  };

  const doUpdate = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        const updatedItem = buildUpdatedItemFromOriginal(questionData);
        onUpdate({ content: [updatedItem] });
        editSnapshotRef.current = null;
        setEditMode(false);
      }
      return;
    }
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = [...contentArray];
    const originalItem = updatedArray[contentIndex];
    const updatedItem = buildUpdatedItemFromOriginal(originalItem);
    updatedArray[contentIndex] = updatedItem;

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }
      if (onUpdate) onUpdate(updated);
      editSnapshotRef.current = null;
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        onUpdate({ content: [] });
        setIsDeleteConfirmOpen(false);
      }
      return;
    }
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = contentArray.filter((_, idx) => idx !== contentIndex);

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setIsDeleteConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => setIsSubmitted(true);
  const handleShowAnswer = () => setShowAnswer(true);

  const Button = ({
    children,
    onClick,
    variant,
    size,
    className,
    disabled,
  }) => {
    const baseClass = "match-button";
    const variantClass = variant ? `match-button--${variant}` : "";
    const sizeClass = size ? `match-button--${size}` : "";
    const disabledClass = disabled ? "match-button--disabled" : "";
    const classes = `${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
      className || ""
    }`;

    return (
      <button className={classes} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  };

  console.log(items, "match");

  return (
    <div className={`match-viewer match-viewer--${editMode ? "edit" : "view"}`}>
      {loading && <GlobalLoader visible={loading} text="Processing..." />}
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          handleDelete={doDelete}
          title="Delete match question"
          message="Are you sure you want to delete this match item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      <div className="match-viewer__header">
        <h3>{title}</h3>
        {category && (
          <span className="question-category-chip">
            {String(category)
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </span>
        )}
      </div>

      <div className="match-viewer__content">
        <div className="match-container">
          {items && items.length >= 2 ? (
            items.map((item, i) => {
              const isRightCol = i > 0;
              const isDraggableColumn = editMode && isRightCol;

              return (
                <div className="match-column" key={item.key || i}>
                  <div className="match-column__header">
                    {editMode ? (
                      <div
                        className={`match-textfield match-textfield--fullwidth match-column-title-input`}
                      >
                        <input
                          className="match-textfield__input"
                          value={item.key || ""}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[i] = {
                              ...newItems[i],
                              key: e.target.value,
                            };
                            setItems(newItems);
                          }}
                          readOnly={false}
                        />
                      </div>
                    ) : (
                      item.key
                    )}
                  </div>

                  <div
                    className={`match-column__body ${
                      !editMode && isSubmitted ? "disabled" : ""
                    }`}
                  >
                    {isDraggableColumn ? (
                      <Reorder.Group
                        axis="y"
                        values={item.values || []}
                        onReorder={(newOrder) => {
                          const newItems = [...items];
                          newItems[i] = {
                            ...newItems[i],
                            values: newOrder,
                          };
                          setItems(newItems);
                        }}
                        style={{
                          margin: "0",
                          padding: "0",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                        className="match-item-container"
                      >
                        {(item.values || []).map((value, indx) => (
                          <Reorder.Item
                            key={`${i}-${indx}`}
                            value={value}
                            className="match-item"
                            dragControls={controls}
                            dragListener={true}
                          >
                            <div
                              className={`match-textfield match-textfield--fullwidth match-item-input`}
                            >
                              <input
                                className="match-textfield__input"
                                value={value || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setItems((prev) => {
                                    const next = [...prev];
                                    const vals = [...(next[i].values || [])];
                                    vals[indx] = val;
                                    next[i] = { ...next[i], values: vals };
                                    return next;
                                  });
                                }}
                                readOnly={false}
                              />
                            </div>
                            <DragIndicatorIcon className="drag-indicator-icon" />
                          </Reorder.Item>
                        ))}
                      </Reorder.Group>
                    ) : (
                      (item.values || []).map((value, indx) => {
                        const isCorrect =
                          !editMode &&
                          isSubmitted &&
                          isRightCol &&
                          initialData[1]?.values?.[indx] ===
                            items[1]?.values?.[indx];

                        const correctnessClass =
                          !editMode && isRightCol && isSubmitted
                            ? isCorrect
                              ? "correct-answer"
                              : "wrong-answer"
                            : "";

                        return (
                          <div
                            key={`${i}-${indx}`}
                            className={`match-item ${correctnessClass}`}
                            ref={elementRef}
                          >
                            <div
                              className={`match-textfield match-textfield--fullwidth match-item-input match-item-input--view`}
                            >
                              <input
                                className="match-textfield__input"
                                value={value || ""}
                                onChange={
                                  editMode
                                    ? (e) => {
                                        const val = e.target.value;
                                        setItems((prev) => {
                                          const next = [...prev];
                                          const vals = [
                                            ...(next[i].values || []),
                                          ];
                                          vals[indx] = val;
                                          next[i] = {
                                            ...next[i],
                                            values: vals,
                                          };
                                          return next;
                                        });
                                      }
                                    : () => {}
                                }
                                readOnly={!editMode}
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div>No match data available</div>
          )}
        </div>

        {explanation && !editMode && (
          <div className="match-explanation">
            <strong>Explanation:</strong> {explanation}
          </div>
        )}
        {editMode && (
          <div className="match-explanation-edit">
            <div
              className={`match-textfield match-textfield--fullwidth match-explanation-input`}
            >
              <label className="match-textfield__label">
                Explanation (optional)
              </label>
              <textarea
                className="match-textfield__input"
                rows={3}
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                readOnly={false}
              />
            </div>
          </div>
        )}
      </div>

      <div className="match-viewer__actions">
        {permission &&
          (pageId || allowEditWithoutPersist) &&
          (editMode ? (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={cancelEditing}
                className="match-viewer__action-button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={doUpdate}
                className="match-viewer__action-button"
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={startEditing}
                className="match-viewer__action-button"
              >
                <EditIcon className="toolbar-button-icon" />
                Edit
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setIsDeleteConfirmOpen(true)}
                className="match-viewer__action-button"
              >
                <DeleteIcon className="toolbar-button-icon" />
                Delete
              </Button>
              {onAddNext && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onAddNext}
                  className="match-viewer__action-button"
                >
                  <AddIcon className="toolbar-button-icon" />
                  Add Next
                </Button>
              )}
            </>
          ))}
      </div>
    </div>
  );
};

export default MatchViewer;
